import { debounce } from 'lodash';
import { enqueueSnackbar } from 'notistack';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';
import { _ } from '/@/locale';
import { setField as setCMField, setRecommendation as setCMRecommendation } from '/@/store/crop-monitoring/actions';
import {
    createRecommendationFromAPI,
    updateRecommendationFromAPI,
} from '/@/store/private-data/recommendations/apiProviders';
import { trackEvent } from '/@/store/tracking';
import { mpTrackEvent } from '/@/store/tracking/mixpanel-events';
import type { Field } from '/@/types/field';
import { reverse } from '/@/utils/router';

/**
 * `draft` is a work-in-progress the wizard keeps updating as the user moves between steps;
 * `read_only` is the finished recommendation, which is what the detail and PDF pages expect.
 */
export type SaveStatus = 'draft' | 'read_only';

/** Only the parts of the crop config this save reads; the rest rides along untouched. */
type CropConfigForSave = {
    products?: number[];
    pestControlPlans?: Record<string, { controlPlanId: number }>;
};

/** The API providers are untyped and destructure these keys; this hook only passes them on. */
type RecommendationForSave = {
    crops: CropConfigForSave[];
    appInstance?: unknown;
    field?: unknown;
    weeds?: unknown;
    weed_v2?: unknown;
    disease?: unknown;
    plague?: unknown;
    seed_treatment?: unknown;
    corn_density?: unknown;
    [key: string]: unknown;
};

type CreateArgs = Parameters<typeof createRecommendationFromAPI>[0];
type UpdateArgs = Parameters<typeof updateRecommendationFromAPI>[0];

export type SavedRecommendation = { id: number } & Record<string, unknown>;

export type SaveRecommendationArgs = {
    recommendation: RecommendationForSave;
    productsWithAPIData: unknown[];
    brand: unknown;
    crop: 'corn' | 'soy';
    pestControlPlans: unknown;
    /** The already-saved recommendation, when updating rather than creating. */
    savedRecommendation?: SavedRecommendation;
    /** Optional because callers may still be loading it; it is only passed through. */
    field?: Field;
    catalogBrands: unknown;
    cropConfig: CropConfigForSave;
    status?: SaveStatus;
    /** Suppressed for draft saves, which happen without the user asking. */
    notify?: boolean;
};

/**
 * Builds and persists a seed recommendation.
 *
 * Shared by the edit page and the v2 wizard so both write identical payloads — in particular
 * the `backup` blob the detail, PDF and share views read back. Don't assemble the body
 * anywhere else.
 */
export const useSaveRecommendation = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    /**
     * Saves are serialised rather than dropped. The wizard keeps a draft while the user works,
     * so a draft can still be in flight when they press "Terminar" — dropping that would leave
     * the primary action doing nothing at all.
     */
    const inFlight = useRef<Promise<SavedRecommendation | undefined> | null>(null);
    /**
     * The stored record, tracked in a ref as well as state: a queued save reads it immediately,
     * where the state value in its closure would still be the pre-save `undefined` and it would
     * create a second recommendation instead of updating the first.
     */
    const savedRef = useRef<SavedRecommendation | undefined>(undefined);
    const [savedRecommendation, setSavedRecommendation] = useState<SavedRecommendation | undefined>(undefined);

    const save = useCallback(
        async ({
            recommendation,
            productsWithAPIData,
            brand,
            crop,
            pestControlPlans,
            savedRecommendation: existing,
            field,
            catalogBrands,
            cropConfig,
            status = 'read_only',
            notify = true,
        }: SaveRecommendationArgs): Promise<SavedRecommendation | undefined> => {
            const run = async (): Promise<SavedRecommendation | undefined> => {
                // Whatever the caller captured, the ref knows whether a record already exists.
                const stored = existing ?? savedRef.current;
                try {
                    const cropRecommendation = recommendation.crops[0];
                    const body = {
                        ...recommendation,
                        crops: [
                            {
                                ...cropRecommendation,
                                [`${crop}Products`]: cropRecommendation?.products,
                                pestControlPlans: Object.values(cropConfig.pestControlPlans || {}).map(
                                    (plan) => plan.controlPlanId
                                ),
                            },
                        ],
                        status,
                        backup: {
                            ...recommendation,
                            status,
                            crops: [
                                {
                                    ...cropRecommendation,
                                    products: cropRecommendation?.products,
                                    productRecommendations: productsWithAPIData,
                                    pestControlPlans,
                                    catalogBrands,
                                },
                            ],
                            weeds: [],
                            field,
                            brand,
                        },
                    };

                    let saved: SavedRecommendation;
                    if (stored) {
                        saved = await updateRecommendationFromAPI({ ...body, id: stored.id } as unknown as UpdateArgs);
                        mpTrackEvent('Seed recommendation updated', {
                            crop,
                            entry_source: 'recommendation-detail',
                            number_of_hybrids: cropRecommendation?.products?.length ?? 0,
                            id: saved.id,
                        });
                    } else {
                        saved = await createRecommendationFromAPI(body as unknown as CreateArgs);
                        mpTrackEvent('Seed recommendation saved', {
                            crop,
                            entry_source: 'recommendation-detail',
                        });
                        dispatch(trackEvent({ key: `recomendation.create.${crop}` }));
                    }

                    if (notify) {
                        enqueueSnackbar(
                            _(
                                '¡Recomendación guardada con éxito! Podés consultarla en la sección “Mis recomendaciones”.'
                            ),
                            {
                                variant: 'success',
                                // notistack passes this straight to the DOM node; its typings don't allow data-*.
                                SnackbarProps: { 'data-testid': 'recommendation-saved-snackbar' } as object,
                            }
                        );
                    }

                    savedRef.current = saved;
                    setSavedRecommendation(saved);
                    if (new URLSearchParams(window.location.search).get('from') === 'cropMonitoring') {
                        dispatch(setCMField(field?.id));
                        dispatch(setCMRecommendation(saved.id));
                        navigate(reverse('cropMonitoring.CropMonitoringCorn'));
                    }
                    return saved;
                } catch (error) {
                    console.error(error);
                    // Without this a failed save is indistinguishable from a dead button: callers
                    // key their navigation on the returned recommendation, so they simply stay put.
                    if (notify) {
                        enqueueSnackbar(_('No pudimos guardar la recomendación. Intentá de nuevo.'), {
                            variant: 'error',
                        });
                    }
                    return undefined;
                }
            };

            // Chain onto any save already running, so they can't interleave on the same record.
            const previous = inFlight.current ?? Promise.resolve(undefined);
            const next = previous.catch(() => undefined).then(run);
            inFlight.current = next;
            try {
                return await next;
            } finally {
                if (inFlight.current === next) inFlight.current = null;
            }
        },
        [dispatch, navigate]
    );

    // The edit page saves on every change; debouncing keeps that to one request per burst.
    const debouncedSave = useMemo(() => debounce(save, 1000), [save]);

    return { save, debouncedSave, savedRecommendation, setSavedRecommendation };
};
